"use strict";
var _a, _b, _c, _d;
Object.defineProperty(exports, "__esModule", { value: true });
exports.UsersService = void 0;
const tslib_1 = require("tslib");
const common_1 = require("@nestjs/common");
const typeorm_1 = require("@nestjs/typeorm");
const typeorm_2 = require("typeorm");
const auth_service_1 = require("../auth/auth.service");
const authorization_service_1 = require("../authorization/authorization.service");
const doctor_fee_config_entity_1 = require("./doctor-fee-config.entity");
const user_entity_1 = require("./user.entity");
let UsersService = class UsersService {
    constructor(userRepo, feeRepo, authService, authorizationService) {
        this.userRepo = userRepo;
        this.feeRepo = feeRepo;
        this.authService = authService;
        this.authorizationService = authorizationService;
    }
    async findAll() {
        const users = await this.userRepo.find({
            order: { createdAt: 'DESC' },
        });
        return this.attachActiveFees(users);
    }
    async findDoctors() {
        const doctors = await this.userRepo.find({
            where: { role: 'doctor', isActive: true },
            select: ['id', 'name', 'email', 'phone', 'role', 'consultationFees', 'priorityConsultationFee'],
            order: { name: 'ASC' },
        });
        return this.attachActiveFees(doctors);
    }
    async getDoctorFeeHistory(id) {
        const user = await this.userRepo.findOne({ where: { id } });
        if (!user)
            throw new common_1.NotFoundException('User not found');
        return this.feeRepo.find({
            where: { doctorId: id },
            order: { createdAt: 'DESC' },
        });
    }
    getRoles() {
        return this.authorizationService.listRoleOptions();
    }
    async create(input) {
        const email = input.email.trim().toLowerCase();
        await this.ensureRoleExists(input.role);
        const existing = await this.userRepo.findOne({ where: { email } });
        if (existing) {
            throw new common_1.BadRequestException('A user with this email already exists');
        }
        const passwordHash = await this.authService.hashPassword(input.password);
        const created = this.userRepo.create({
            name: input.name.trim(),
            email,
            passwordHash,
            role: input.role.trim().toLowerCase(),
            phone: input.phone?.trim() || null,
            consultationFees: this.normalizeConsultationFees(input.consultationFees),
            priorityConsultationFee: Number(input.priorityConsultationFee ?? 0),
            isActive: input.isActive ?? true,
        });
        const saved = await this.userRepo.save(created);
        await this.syncDoctorFees(saved.id, saved.role, {
            consultationFees: input.consultationFees,
            priorityConsultationFee: input.priorityConsultationFee,
        });
        return this.findOnePublic(saved.id);
    }
    async update(id, input) {
        const user = await this.userRepo.findOne({ where: { id } });
        if (!user)
            throw new common_1.NotFoundException('User not found');
        const email = input.email.trim().toLowerCase();
        const duplicate = await this.userRepo.findOne({ where: { email } });
        if (duplicate && duplicate.id !== id) {
            throw new common_1.BadRequestException('A user with this email already exists');
        }
        await this.ensureRoleExists(input.role);
        user.name = input.name.trim();
        user.email = email;
        user.role = input.role.trim().toLowerCase();
        user.phone = input.phone?.trim() || null;
        user.consultationFees = this.normalizeConsultationFees(input.consultationFees);
        user.priorityConsultationFee = Number(input.priorityConsultationFee ?? 0);
        user.isActive = input.isActive;
        const saved = await this.userRepo.save(user);
        await this.syncDoctorFees(saved.id, saved.role, {
            consultationFees: input.consultationFees,
            priorityConsultationFee: input.priorityConsultationFee,
        });
        return this.findOnePublic(saved.id);
    }
    async ensureRoleExists(roleKey) {
        const roles = await this.authorizationService.listRoleOptions();
        if (!roles.some(role => role.key === roleKey.trim().toLowerCase())) {
            throw new common_1.BadRequestException('Selected role does not exist');
        }
    }
    normalizeConsultationFees(fees) {
        if (!fees || typeof fees !== 'object')
            return null;
        return {
            video: Number(fees.video ?? 0),
            phone: Number(fees.phone ?? 0),
            clinic: Number(fees.clinic ?? 0),
            outlet: Number(fees.outlet ?? 0),
        };
    }
    async findOnePublic(id) {
        const user = await this.userRepo.findOne({ where: { id } });
        if (!user)
            throw new common_1.NotFoundException('User not found');
        const [withFees] = await this.attachActiveFees([user]);
        return withFees;
    }
    async attachActiveFees(users) {
        if (!users.length)
            return users;
        const doctorIds = users.filter(user => user.role === 'doctor').map(user => user.id);
        if (!doctorIds.length)
            return users;
        const activeConfigs = await this.feeRepo.find({
            where: doctorIds.map(id => ({ doctorId: id, isActive: true })),
            order: { createdAt: 'DESC' },
        });
        const configMap = new Map();
        for (const config of activeConfigs) {
            if (!configMap.has(config.doctorId))
                configMap.set(config.doctorId, config);
        }
        return users.map(user => {
            const active = configMap.get(user.id);
            if (!active)
                return user;
            return {
                ...user,
                consultationFees: active.consultationFees,
                priorityConsultationFee: Number(active.priorityConsultationFee ?? 0),
            };
        });
    }
    async syncDoctorFees(userId, role, input) {
        const normalizedFees = this.normalizeConsultationFees(input.consultationFees);
        const priorityFee = Number(input.priorityConsultationFee ?? 0);
        if (role !== 'doctor') {
            await this.feeRepo.update({ doctorId: userId, isActive: true }, {
                isActive: false,
                effectiveTo: new Date(),
            });
            await this.userRepo.update(userId, {
                consultationFees: null,
                priorityConsultationFee: 0,
            });
            return;
        }
        const existing = await this.feeRepo.findOne({
            where: { doctorId: userId, isActive: true },
            order: { createdAt: 'DESC' },
        });
        const hasChanged = !existing
            || JSON.stringify(existing.consultationFees ?? {}) !== JSON.stringify(normalizedFees ?? {})
            || Number(existing.priorityConsultationFee ?? 0) !== priorityFee;
        if (hasChanged) {
            if (existing) {
                await this.feeRepo.update(existing.id, {
                    isActive: false,
                    effectiveTo: new Date(),
                });
            }
            await this.feeRepo.save(this.feeRepo.create({
                doctorId: userId,
                consultationFees: normalizedFees ?? { video: 0, phone: 0, clinic: 0, outlet: 0 },
                priorityConsultationFee: priorityFee,
                isActive: true,
                effectiveFrom: new Date(),
            }));
        }
        await this.userRepo.update(userId, {
            consultationFees: normalizedFees,
            priorityConsultationFee: priorityFee,
        });
    }
};
exports.UsersService = UsersService;
exports.UsersService = UsersService = tslib_1.__decorate([
    (0, common_1.Injectable)(),
    tslib_1.__param(0, (0, typeorm_1.InjectRepository)(user_entity_1.User)),
    tslib_1.__param(1, (0, typeorm_1.InjectRepository)(doctor_fee_config_entity_1.DoctorFeeConfig)),
    tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _a : Object, typeof (_b = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _b : Object, typeof (_c = typeof auth_service_1.AuthService !== "undefined" && auth_service_1.AuthService) === "function" ? _c : Object, typeof (_d = typeof authorization_service_1.AuthorizationService !== "undefined" && authorization_service_1.AuthorizationService) === "function" ? _d : Object])
], UsersService);
